// Lišta kategorií + mega menu (screens/01-header.md §4).
//
// Nativní `#navigation` u Jiposu NENÍ ploché — nese celé 3 úrovně
// (`ul.menu-level-1 > li > ul.menu-level-2 > li.has-third-level > div > ul.menu-level-3`),
// takže data pro panel čteme přímo z něj a nic nedomýšlíme. Nativní `<nav>`
// zůstává v DOM (mobilní drawer, issue #7), na desktopu ho skrývá CSS až ve chvíli,
// kdy je naše lišta postavená (`html.bs-header-ready`) — když JS spadne, uživateli
// zůstane funkční nativní menu.
//
// Kurace: v liště jsou jen kategorie z mockupu, ale levý sloupec panelu nabízí
// VŠECHNY root kategorie, aby se z headeru nedalo ztratit nic, co dnes v menu je.

import { iconHtml } from './icon.js';
import { CAT_ICONS, groupIcon, pathOf, readMenu } from './menuModel.js';
import { byPage, byRoot, page } from './pages.js';
import { activeCampaign, saleLabel } from './campaign.js';
import SHOP from '../../config/shop.js';
import L from '../locale/cs/megaMenu.js';

// Kategorie v liště: mockup jich má 5 natvrdo, my vykreslíme VŠECHNY root
// kategorie a necháme jich viditelných tolik, kolik se do řady vejde
// (rozhodnutí 2026-07-30) — na širokém monitoru je zbytečné držet pět položek
// a půl lišty prázdné. Co se nevejde, zůstává dostupné v levém sloupci panelu.
// Slevoakce má vlastní chip, takže se mezi kategoriemi neopakuje.
const BAR_SKIP = [page('sale')];

// Hover intent: ZAVŘENÝ panel se otevírá až po krátké prodlevě, aby pouhé
// přejetí myší přes lištu — třeba ze searchboxu na produkt — menu nevystřelilo
// (zadání Tomáše 2026-09-09, vzor nativní Shoptet `submenuTimeout` 150 ms
// na staré šabloně ostré). Odjetí z prvku i z lišty čekání ruší; klávesnice
// (focus) a klik otevírají hned. Je-li panel už otevřený, uživatel se v menu
// pohybuje a přepínání kategorií (lišta i levý sloupec) je OKAMŽITÉ —
// prodleva tam působila jako lag (upřesnění Tomáše týž den).
const HOVER_OPEN_DELAY = 150;

// Barevné chipy vedle tlačítka „Všechny kategorie".
// „Jipos Tools" je reálná značka shopu, ne kategorie; slug se liší per shop
// (manifest `brands.own`: CZ /znacka/jipos/, SK /znacka/jipos-7/).
// Sdílené s mobilním drawerem (mobileNav.js) — stejné akcenty na obou režimech.
export const CHIPS = [
  { href: page('sale'), label: L.saleChip, icon: 'percent', variant: 'sale' },
  // „Jipos Tools" je reálná značka shopu (proper noun) — nepřekládá se.
  { href: page('brands', SHOP.brands.own + '/'), label: 'Jipos Tools', icon: 'bolt', variant: 'tools' },
];

// Slevoakce má v panelu vlastní odkaz dole pod seznamem → v seznamu se neopakuje.
const RAIL_SKIP = [page('sale')];

// CTA v posledním sloupci (skloňování dle jazyka, texty z mockupu — L.cta).
const CTA = byRoot(L.cta);

// Promo karta vpravo v panelu (screens/01-header.md §4). Podklady jsou nahrané
// v adminu přes deploy/upload-media.ps1 a v 04-assets.md označené „SiteHeader".
// Bez obrázku se karta nevykreslí — prázdná plocha by byla horší než nic.
//
// Od 2026-08-31 má lifestyle banner KAŽDÁ root kategorie (zadání Tomáše).
// Chybějící podklady (vč. vadných fotek Dílny a Stavby z issue #81 — logo
// Makita, resp. vodoznak) nahradily generované lifestyle fotky `mega-*.jpg`
// (Higgsfield, bez textů a log ve scéně).
import { MEDIA } from './instanceUrl.js';

// Klíč = kořen katalogu (`catalog.roots` z manifestu shopu), výprodej = stránka.
const PROMO = {
  ...byRoot({
    garden: {
      img: MEDIA + 'hf_20260712_124215_8f6b905c-b2cb-45d6-bd04-1c70d74b082a.webp',
      ...L.promo.garden,
    },
    build: {
      img: MEDIA + 'mega-stavba.jpg',
      ...L.promo.build,
    },
    workshop: {
      img: MEDIA + 'mega-dilna.jpg',
      ...L.promo.workshop,
    },
    home: {
      img: MEDIA + 'hf_20260714_185017_30253ebb-0477-491c-bd21-d6f0af3bdf30.webp',
      ...L.promo.home,
    },
    kids: {
      img: MEDIA + 'hf_20260714_185019_ddfd470e-ac82-4b75-9584-657b407c00d2.webp',
      ...L.promo.kids,
    },
    car: {
      img: MEDIA + 'mega-autodoplnky.jpg',
      ...L.promo.car,
    },
    xmas: {
      img: MEDIA + 'mega-vanoce.jpg',
      ...L.promo.xmas,
    },
    ppe: {
      img: MEDIA + 'mega-oopp.jpg',
      ...L.promo.ppe,
    },
    solar: {
      img: MEDIA + 'mega-fotovoltaika.jpg',
      ...L.promo.solar,
    },
  }),
  ...byPage({
    clearance: {
      img: MEDIA + 'mega-vyprodej.jpg',
      ...L.promo.clearance,
    },
  }),
};

const MAX_COLS = 8; // 2 řady po 4 sloupcích
const MAX_LINKS = 5;
const MAX_REST = 8;

const LABEL_ALL = L.labelAll;
const LABEL_REST = L.labelRest;

// --------------------------------------------------------------- model
// Čtení nativního menu žije v menuModel.js (sdílí ho mobilní drawer).

// Root kategorie aktuální stránky — z URL, jinak z drobečkové navigace
// (podkategorie mají ploché URL, root se pozná jen z breadcrumbs).
function currentRootPath(roots) {
  const here = window.location.pathname;
  const paths = roots.map((r) => r.path);
  if (paths.indexOf(here) !== -1) return here;
  for (const a of document.querySelectorAll('.breadcrumbs a[href]')) {
    const p = pathOf(a.getAttribute('href'));
    if (paths.indexOf(p) !== -1) return p;
  }
  return null;
}

// --------------------------------------------------------------- panel

function buildColumns(root) {
  const cols = [];
  const rest = [];
  for (const g of root.groups) {
    if (g.links.length) cols.push({ title: g, links: g.links.slice(0, MAX_LINKS) });
    else rest.push(g);
  }
  if (rest.length) {
    cols.push({ title: { label: LABEL_REST, href: root.href }, links: rest.slice(0, MAX_REST) });
  }
  return cols.slice(0, MAX_COLS);
}

function buildContent(root) {
  const content = document.createElement('div');
  content.className = 'bs-mega__content';

  const grid = document.createElement('div');
  grid.className = 'bs-mega__cols';
  const cols = buildColumns(root);
  cols.forEach(function (col, i) {
    const box = document.createElement('div');
    box.className = 'bs-mega__col';

    // Ikona u titulku skupiny (2026-07-30) — heuristika podle názvu, fallback
    // je ikona root kategorie, takže sloupec nikdy nezůstane bez glyphu.
    const title = document.createElement('a');
    title.className = 'bs-mega__col-title';
    title.href = col.title.href;
    title.innerHTML = iconHtml(groupIcon(col.title.label, CAT_ICONS[root.path]));
    const titleLabel = document.createElement('span');
    titleLabel.textContent = col.title.label;
    title.appendChild(titleLabel);
    box.appendChild(title);

    for (const link of col.links) {
      const a = document.createElement('a');
      a.className = 'bs-mega__link';
      a.href = link.href;
      a.textContent = link.label;
      box.appendChild(a);
    }

    if (i === cols.length - 1) {
      const cta = document.createElement('a');
      cta.className = 'bs-mega__cta';
      cta.href = root.href;
      cta.textContent = CTA[root.path] || L.ctaFallback(root.label);
      box.appendChild(cta);
    }
    grid.appendChild(box);
  });
  content.appendChild(grid);

  const promo = PROMO[root.path];
  if (promo && promo.img) {
    const card = document.createElement('a');
    card.className = 'bs-mega__promo';
    card.href = promo.href || root.href;
    card.innerHTML =
      '<span class="bs-mega__promo-img"></span>' +
      '<span class="bs-mega__promo-shade"></span>' +
      '<span class="bs-mega__promo-text">' +
      '<span class="bs-mega__promo-badge"></span>' +
      '<span class="bs-mega__promo-title"></span>' +
      '</span>';
    // pozadí přes style property, ne přes `style="…"` v innerHTML — uvozovky
    // z JSON.stringify by atribut ukončily a obrázek by se nikdy nenačetl
    // karta výprodeje skladu po dobu kampaně nese claim („Výprodej skladu až −30 %"),
    // kampaň s vlastním vizuálem (Black Friday) i jeho obrázek a název kampaně
    const campaign = root.path === page('clearance') ? activeCampaign() : null;
    const media = campaign && campaign.media;
    card.querySelector('.bs-mega__promo-img').style.backgroundImage = 'url("' + (media ? MEDIA + media.mega : promo.img) + '")';
    card.querySelector('.bs-mega__promo-badge').textContent = media ? campaign.name : promo.badge;
    card.querySelector('.bs-mega__promo-title').textContent = (campaign && campaign.clearance) || promo.title;
    content.appendChild(card);
  }

  return content;
}

const BP_XL = 1024; // hranice desktopové lišty (_tokens.scss $bp-xl, _nav.scss mq-max(xl))

// ⚠ Lazy, ne na modulové úrovni — devcheck importuje moduly do Node kvůli unit
// testům a `window` tam neexistuje (regrese chycená 2026-09-11).
let mqNoBar;
function barHidden() {
  if (mqNoBar === undefined) {
    mqNoBar = window.matchMedia ? window.matchMedia('(max-width: ' + BP_XL + 'px)') : null;
  }
  return !!(mqNoBar && mqNoBar.matches);
}

// Kolik kategorií se do lišty vejde. Měříme skutečné šířky proti volnému místu
// mezi tlačítkem „Všechny kategorie" a koncovým blokem (separátor + chipy) —
// spolehlivější než scrollWidth, protože položky mají `flex: none` a lišta by
// místo přetečení jen posunula chipy mimo container.
function fitBar(bar, allBtn, cats, tail) {
  if (!cats.length) return;
  for (const a of cats) a.classList.remove('is-overflow');

  // ⚠ Nejdřív VŠECHNA čtení, pak teprve zápisy. Střídat `offsetWidth`
  // s `classList.add` v jedné smyčce = layout thrashing: každý zápis znečistil
  // layout, který další čtení znovu vynutilo — nad 14 tis. uzly to stálo
  // 486–632 ms self-time při CPU 4× (profil 2026-09-11, F1).
  const gap = parseFloat(getComputedStyle(bar).columnGap) || 0;
  let avail = bar.clientWidth - allBtn.offsetWidth - gap;
  if (tail) avail -= tail.offsetWidth + gap;
  const widths = cats.map(function (a) {
    return a.offsetWidth + gap;
  });

  // První položka, která se nevejde, uřízne i zbytek — jinak by se přes ni
  // „prolezla" kratší kategorie za ní a v pořadí menu by vznikla díra.
  let used = 0;
  let full = false;
  for (let i = 0; i < cats.length; i += 1) {
    const w = widths[i];
    if (full || used + w > avail) {
      cats[i].classList.add('is-overflow');
      full = true;
    } else {
      used += w;
    }
  }
}

// --------------------------------------------------------------- init

export function initMegaMenu() {
  const host = document.querySelector('#header .header-bottom-wrapper')
    || document.querySelector('#header .header-bottom .container');
  if (!host || host.querySelector('.bs-catbar')) return;

  const roots = readMenu();
  if (!roots.length) return;
  const byPath = {};
  for (const r of roots) byPath[r.path] = r;

  const bar = document.createElement('div');
  bar.className = 'bs-catbar';

  // tmavé tlačítko „Všechny kategorie"
  const allBtn = document.createElement('button');
  allBtn.type = 'button';
  allBtn.className = 'bs-catbar__all';
  allBtn.setAttribute('aria-expanded', 'false');
  allBtn.setAttribute('aria-controls', 'bs-mega');
  allBtn.innerHTML =
    iconHtml('grid') + '<span>' + LABEL_ALL + '</span>' + iconHtml('chevron-down', 'bs-catbar__chev');
  bar.appendChild(allBtn);

  const currentPath = currentRootPath(roots);
  const barLinks = {};
  const barCats = [];
  for (const root of roots) {
    if (BAR_SKIP.indexOf(root.path) !== -1) continue;
    const a = document.createElement('a');
    a.className = 'bs-catbar__cat';
    a.href = root.href;
    a.innerHTML =
      iconHtml(CAT_ICONS[root.path] || 'grid') +
      '<span class="bs-catbar__label"></span>' +
      iconHtml('chevron-down', 'bs-catbar__chev');
    const label = a.querySelector('.bs-catbar__label');
    label.textContent = root.label;
    label.setAttribute('data-label', root.label); // ghost dvojník šířky; setAttribute = bez escapování ručně
    if (root.path === currentPath) a.classList.add('is-current');
    barLinks[root.path] = a;
    barCats.push(a);
    bar.appendChild(a);
  }

  // Slevoakce a Jipos Tools narušovaly flow navigace mezi „Všechny kategorie"
  // a kategoriemi → jdou na konec řady, oddělené separátorem (2026-07-30).
  const tail = document.createElement('div');
  tail.className = 'bs-catbar__tail';

  const sep = document.createElement('span');
  sep.className = 'bs-catbar__sep';
  sep.setAttribute('aria-hidden', 'true');
  tail.appendChild(sep);

  for (const chip of CHIPS) {
    if (chip.href.indexOf(page('brands')) === 0 || byPath[chip.href]) {
      const a = document.createElement('a');
      a.className = 'bs-catbar__chip bs-catbar__chip--' + chip.variant;
      a.href = chip.href;
      a.innerHTML = '<span class="bs-catbar__dot">' + iconHtml(chip.icon) + '</span>';
      // po dobu kampaně čip nese název deštníku („Říjnový výprodej")
      a.appendChild(document.createTextNode((chip.variant === 'sale' && saleLabel()) || chip.label));
      tail.appendChild(a);
    }
  }
  bar.appendChild(tail);

  // --- panel ---
  const mega = document.createElement('div');
  mega.className = 'bs-mega';
  mega.id = 'bs-mega';
  mega.setAttribute('aria-hidden', 'true');
  const panel = document.createElement('div');
  panel.className = 'bs-mega__panel';
  const rail = document.createElement('div');
  rail.className = 'bs-mega__rail';
  panel.appendChild(rail);
  mega.appendChild(panel);
  bar.appendChild(mega);

  const railLinks = {};
  for (const root of roots) {
    if (RAIL_SKIP.indexOf(root.path) !== -1) continue;
    const a = document.createElement('a');
    a.className = 'bs-mega__rail-item';
    a.href = root.href;
    a.innerHTML =
      iconHtml(CAT_ICONS[root.path] || 'grid') +
      '<span class="bs-mega__rail-label"></span>' +
      iconHtml('chevron-down', 'bs-mega__rail-chev');
    a.querySelector('.bs-mega__rail-label').textContent = root.label;
    a.addEventListener('mouseenter', function () {
      scheduleOpen(root.path);
    });
    a.addEventListener('mouseleave', cancelOpen);
    a.addEventListener('focus', function () {
      open(root.path);
    });
    railLinks[root.path] = a;
    rail.appendChild(a);
  }

  const sale = byPath[page('sale')];
  if (sale) {
    const a = document.createElement('a');
    a.className = 'bs-mega__rail-sale';
    a.href = sale.href;
    a.innerHTML = iconHtml('percent') + '<span></span>';
    a.querySelector('span').textContent = sale.label;
    rail.appendChild(a);
  }

  const contentCache = {};
  let activePath = null;
  let openTimer = 0;

  function cancelOpen() {
    if (openTimer) {
      clearTimeout(openTimer);
      openTimer = 0;
    }
  }

  function scheduleOpen(path) {
    cancelOpen();
    if (mega.classList.contains('is-open')) {
      open(path);
      return;
    }
    openTimer = setTimeout(function () {
      openTimer = 0;
      open(path);
    }, HOVER_OPEN_DELAY);
  }

  function open(path) {
    cancelOpen();
    const root = byPath[path];
    if (!root) return;
    if (activePath === path) {
      mega.classList.add('is-open');
      return;
    }
    if (!contentCache[path]) contentCache[path] = buildContent(root);
    const prev = panel.querySelector('.bs-mega__content');
    if (prev) prev.remove();
    panel.appendChild(contentCache[path]);

    for (const p in railLinks) railLinks[p].classList.toggle('is-active', p === path);
    for (const p in barLinks) barLinks[p].classList.toggle('is-active', p === path);

    activePath = path;
    mega.classList.add('is-open');
    mega.setAttribute('aria-hidden', 'false');
    allBtn.setAttribute('aria-expanded', 'true');
  }

  function close() {
    cancelOpen();
    if (!mega.classList.contains('is-open')) return;
    mega.classList.remove('is-open');
    mega.setAttribute('aria-hidden', 'true');
    allBtn.setAttribute('aria-expanded', 'false');
    for (const p in railLinks) railLinks[p].classList.remove('is-active');
    for (const p in barLinks) barLinks[p].classList.remove('is-active');
  }

  const firstPath = roots.filter(function (r) {
    return BAR_SKIP.indexOf(r.path) === -1;
  }).map(function (r) {
    return r.path;
  })[0] || roots[0].path;

  allBtn.addEventListener('mouseenter', function () {
    scheduleOpen(activePath || firstPath);
  });
  allBtn.addEventListener('mouseleave', cancelOpen);
  allBtn.addEventListener('click', function () {
    if (mega.classList.contains('is-open')) close();
    else open(activePath || firstPath);
  });
  for (const path in barLinks) {
    barLinks[path].addEventListener('mouseenter', function () {
      scheduleOpen(path);
    });
    barLinks[path].addEventListener('mouseleave', cancelOpen);
  }
  for (const chip of bar.querySelectorAll('.bs-catbar__chip')) {
    chip.addEventListener('mouseenter', close);
  }
  bar.addEventListener('mouseleave', close);
  document.addEventListener('keydown', function (e) {
    if (e.key === 'Escape' && mega.classList.contains('is-open')) {
      close();
      allBtn.focus();
    }
  });
  document.addEventListener('click', function (e) {
    if (!mega.classList.contains('is-open')) return;
    if (!bar.contains(e.target)) close();
  });

  host.insertBefore(bar, host.firstChild);

  // Kolik kategorií se vejde, se pozná až po layoutu. Přepočítáváme na resize
  // a na sticky kompakci headeru (mění výšku i písmo lišty).
  const refit = function () {
    // Pod `xl` lištu skrývá CSS (`_nav.scss`, `@media (max-width: 1024px)`) —
    // měřit šířky skrytého pruhu je čistá práce navíc a na mobilu to byl po
    // odložení zbytku bootu největší jednotlivý náklad (575–791 ms při CPU 4×).
    if (barHidden()) return;
    fitBar(bar, allBtn, barCats, tail);
  };
  // ⚠ Synchronní fit hned po `insertBefore` vynucoval layout nad 14 tis. uzly
  // uprostřed boot sekvence (486–632 ms self-time při CPU 4×, profil 2026-09-11).
  // ResizeObserver vydá první observaci sám — po layoutu, ještě před paintem, takže
  // lišta nebliskne. Bez RO (starý prohlížeč) stačí rAF, taky po layoutu.
  if (window.ResizeObserver) new ResizeObserver(refit).observe(bar);
  else {
    requestAnimationFrame(refit);
    window.addEventListener('resize', refit);
  }
  // Getter `document.fonts.ready` vynutí přepočet stylu: uprostřed bootu tedy nad
  // stránkou ještě ve stavu kostry, kterou odkrytí stejně přepočítá znovu (35–76 ms
  // při CPU 4×, profil 2026-10-13, ops/sweeps/2026-10-13-lcp-kostra/boot-profile.mjs).
  // rAF ho posune za konec bootu, do snímku, který styl počítá tak jako tak.
  if (document.fonts) {
    requestAnimationFrame(function () {
      if (document.fonts.ready) document.fonts.ready.then(refit);
    });
  }
}
